/**
 * AutoSaveContext - Contexto para compartir el estado de auto-guardado
 * entre los formularios del vault y el sidebar.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { AutoSaveState } from '../hooks/useVaultAutoSave';

interface AutoSaveContextValue {
  /** Estado actual del auto-guardado */
  autoSaveState: AutoSaveState;
  /** Actualizar el estado desde los formularios */
  setAutoSaveState: (state: AutoSaveState) => void;
  /** Resetear el estado (cuando cambia de requirement) */
  resetAutoSaveState: () => void;
}

const defaultState: AutoSaveState = {
  lastSaved: null,
  hasStoredData: false,
  isSaving: false,
  hasPendingChanges: false,
};

const AutoSaveContext = createContext<AutoSaveContextValue | null>(null);

interface AutoSaveProviderProps {
  children: ReactNode;
}

export function AutoSaveProvider({ children }: AutoSaveProviderProps) {
  const [autoSaveState, setAutoSaveStateInternal] = useState<AutoSaveState>(defaultState);

  const setAutoSaveState = useCallback((state: AutoSaveState) => {
    setAutoSaveStateInternal(state);
  }, []);

  const resetAutoSaveState = useCallback(() => {
    setAutoSaveStateInternal(defaultState);
  }, []);

  return (
    <AutoSaveContext.Provider
      value={{
        autoSaveState,
        setAutoSaveState,
        resetAutoSaveState,
      }}
    >
      {children}
    </AutoSaveContext.Provider>
  );
}

export function useAutoSaveContext() {
  const context = useContext(AutoSaveContext);
  if (!context) {
    throw new Error('useAutoSaveContext must be used within an AutoSaveProvider');
  }
  return context;
}

/**
 * Hook opcional que retorna null si no hay provider
 * Útil para componentes que pueden existir fuera del contexto
 */
export function useAutoSaveContextOptional() {
  return useContext(AutoSaveContext);
}
